import { useEffect, useState, useRef, useMemo } from 'react';
import { IDriverUser } from '../../types/type';
import Spiner from '../Spiner/Spiner';
import './driverRoadLists.css';
import { RoadListService, RoadListFilters, IRoadListItem } from '../../services/roadlist.service';
import { CSSTransition } from 'react-transition-group';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import Pagination from '../Pagination/Pagination';
import SimpleManyPhotoContainer from '../SimpleManyPhotoContainer/SimpleManyPhotoContainer';
import DateRangeFilter from '../DateRangeFilter/DateRangeFilter';
import { useFilters, PaginatedFilters } from '../../hooks/useFilters';
import Table, { ColumnConfig, ExpandedRowConfig, SecondaryRowConfig, LabeledCell } from '../Table/Table';
import StatusCell from '../Table/StatusCell';

interface IProps {
  driver: IDriverUser;
}

// Расширяем интерфейс RoadListFilters, чтобы он соответствовал PaginatedFilters
interface ExtendedRoadListFilters extends PaginatedFilters {
  // Дополнительные поля, если они есть в RoadListFilters
}

export default function DriverRoadLists({ driver }: IProps) {
  const [roadLists, setRoadLists] = useState<IRoadListItem[]>([]);
  const [openRows, setOpenRows] = useState<boolean[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { filters, handleFilterChange, resetFilters, handlePageChange } = useFilters<ExtendedRoadListFilters>({
    page: 1,
    limit: 10,
    startTime: '',
    endTime: '',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Создаем массив ссылок для анимации
  const row2Refs = useRef<(HTMLElement | null)[]>([]);
  const row3Refs = useRef<(HTMLElement | null)[]>([]);
  const row4Refs = useRef<(HTMLElement | null)[]>([]);

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Оформлен', dotClass: 'active' },
    rejected: { label: 'Не оформлен', dotClass: 'rejected' },
    finished: { label: 'Оформлен', dotClass: 'active' },
    waiting: { label: 'Ожидает', dotClass: 'waiting' },
    inProgress: { label: 'В процессе', dotClass: 'inProgress' },
    canceled: { label: 'Отменен', dotClass: 'canceled' },
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.date)}</div>
        </>
      ),
    },
    {
      id: 'num',
      title: 'Номер заявки',
      render: (row) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.date)}</div>
        </>
      ),
    },
    {
      id: 'car',
      title: 'Номер авто',
      render: (row) => (
        <>
          <div className="table-cell-title">Номер авто</div>
          <div className="table-cell-value">{row.car?.number}</div>
        </>
      ),
    },
    {
      id: 'status',
      title: 'Статус',
      render: (row) => {
        return <StatusCell status={row.status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null, // Рендеринг происходит внутри компонента Table
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'details1',
      content: (row: IRoadListItem) => (
        <>
          <LabeledCell
            title="Тех осмотр"
            value={
              row.techlist.checkStatus === 'completed'
                ? 'Положительный'
                : row.techlist.checkStatus === 'rejected'
                  ? 'Отрицательный'
                  : 'Ожидает'
            }
            colSpan={1}
          />
          <LabeledCell
            title="Статус"
            value={row.status === 'completed' ? 'Оформлен' : row.status === 'rejected' ? 'Не оформлен' : 'Ожидает'}
          />

          <LabeledCell
            title="Мед. проверка"
            value={
              row.medlist.status === 'completed'
                ? 'Пройдена'
                : row.medlist.status === 'rejected'
                  ? 'Не пройдена'
                  : 'Ожидает'
            }
            colSpan={1}
          />
          <LabeledCell colSpan={1} title="Марка авто" value={row.car?.mark || 'Марка'} />
          <LabeledCell colSpan={1} title="Модель авто" value={row.car?.model || 'Модель'} />
          <LabeledCell
            title="Одометр"
            value={`${row.techlist.odometerReading ? row.techlist.odometerReading : '-'} км`}
            colSpan={1}
          />
        </>
      ),
    },
    {
      id: 'details2',
      content: (row: IRoadListItem) => (
        <>
          <LabeledCell title="Автопарк" value={row.autopark?.legalName || 'Автопарк'} />
        </>
      ),
    },
    // {
    //   id: 'details3',
    //   content: (row) => (
    //     <>
    //       <LabeledCell
    //         title="Одометр"
    //         value={`${row.techlist.odometerReading ? row.techlist.odometerReading : '-'} км`}
    //         colSpan={6}
    //       />
    //     </>
    //   ),
    // },
  ];

  // Подсчет статистики путевых листов
  const roadListStats = useMemo(() => {
    const totalCount = roadLists?.length || 0;
    const completedCount = roadLists?.filter((list) => list.status === 'completed').length || 0;
    const uncompletedCount = roadLists?.filter((list) => list.status === 'rejected').length || 0;
    const waitingCount = roadLists?.filter((list) => list.status === 'waiting').length || 0;

    return {
      totalCount,
      completedCount,
      uncompletedCount,
      waitingCount,
    };
  }, [roadLists]);

  // Функция для переключения строки (раскрытие/скрытие деталей)
  const toggleRow = (index: number) => {
    setOpenRows((prev) => {
      const newRows = [...prev];
      newRows[index] = !newRows[index];
      return newRows;
    });
  };

  // Загрузка данных с учетом фильтров
  useEffect(() => {
    async function fetchRoadLists() {
      if (!driver?.id) return;

      setLoading(true);
      setError('');

      try {
        // Приводим фильтры к нужному типу RoadListFilters перед передачей
        const apiFilters: RoadListFilters = {
          page: filters.page,
          limit: filters.limit,
          startTime: filters.startTime,
          endTime: filters.endTime,
        };

        const response = await RoadListService.getDriverRoadLists(driver.id, apiFilters);
        setRoadLists(response.data || []);

        // Вычисляем количество страниц
        setPagination({
          currentPage: filters.page || 1,
          totalPages: response.totalPages || 1,
          totalCount: response.totalCount || 0,
        });

        // Инициализируем состояние открытых строк
        setOpenRows(Array((response.data || []).length).fill(false));

        // Инициализируем ссылки для анимации
        row2Refs.current = Array((response.data || []).length).fill(null);
        row3Refs.current = Array((response.data || []).length).fill(null);
        row4Refs.current = Array((response.data || []).length).fill(null);
      } catch (error) {
        console.error('Ошибка при загрузке путевых листов:', error);
        setError('Не удалось загрузить данные о путевых листах');

        // Сбрасываем данные при ошибке
        setRoadLists([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchRoadLists();
  }, [driver, filters]);

  if (loading) return <Spiner />;

  if (error) return <div className="errorMessage">{error}</div>;

  return (
    <div className="driverRoadLists">
      <div className="infoCard">
        {/* Блок фильтров */}
        <DateRangeFilter
          startTime={filters.startTime}
          endTime={filters.endTime}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          className="roadFilters"
        />

        {/* Блок статистики путевых листов */}
        <div className="roadStatsContainer" style={{ padding: '10px 25px' }}>
          <div className="roadStatsText" style={{ fontWeight: 'bold', color: '#333' }}>
            Путевых листов: {roadListStats.totalCount}, из них Оформленных {roadListStats.completedCount}, Не
            оформленных {roadListStats.uncompletedCount}, Ожидают {roadListStats.waitingCount}
          </div>
        </div>

        {loading ? (
          <div className="loadingIndicator">Загрузка...</div>
        ) : error ? (
          <div className="errorMessage">{error}</div>
        ) : (
          <>
            <div className="admListContainer">
              <Table
                data={roadLists}
                columns={tableColumns}
                expandedRows={expandedRows}
                keyField="id"
                noDataMessage="Путевые листы не найдены"
                // className="roadlist-table"
              />
            </div>

            {/* Пагинация */}
            {pagination.totalPages > 1 && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
